/**
 * FlahaSOIL v2 — ProfessionalReportPreview (Phase 10D.1).
 *
 * High-fidelity, print-ready renderer for professional soil reports.
 * Integrates:
 *   - ReportCoverPage (page 1, break-after: page)
 *   - Scientific charts from Phase 10C-D
 *   - Numeric formatting for SAR/ESP/CEC
 *   - Scientific disclaimers
 *   - Print-safe CSS via PrintStyles
 */
import { Box, Stack, Typography, Grid } from "@mui/material";
import {
  reportLevelScope,
  type ProfessionalReportDTO,
} from "@flaha/shared-types";

import { flahaSoilColors } from "../../../../theme/flahaSoilTheme";
import { ReportFooter } from "../../../reports/components/ReportFooter";
import { ReportTable } from "../../../reports/components/ReportTable";
import { StatusBadge } from "../../../reports/components/StatusBadge";
import {
  AgronomicCategoriesBlock,
  NotesBlock,
  RecommendationGroup,
  SuitabilityBlock,
} from "../../../reports/components/ReportSnapshotBlocks";

import { PrintStyles } from "./PrintStyles";
import { ReportCoverPage } from "./ReportCoverPage";
import { ReportSection } from "./ReportSection";
import { ScientificDisclaimer } from "./ScientificDisclaimer";
import { ReportChartsSection } from "./ReportChartsSection";

/**
 * Display-only numeric formatter for soil report values.
 * Does NOT change stored/calculated values.
 * - integers: no decimal
 * - 0–10 range: 2 decimal places
 * - >10: 1 decimal place
 * - non-finite / null / undefined: "—"
 */
function fmtSoil(value: number | null | undefined, decimals?: number): string {
  if (value === null || value === undefined) return "—";
  if (!Number.isFinite(value)) return "—";
  if (decimals !== undefined) return value.toFixed(decimals);
  if (Number.isInteger(value)) return value.toString();
  return Math.abs(value) < 10 ? value.toFixed(2) : value.toFixed(1);
}

export interface ProfessionalReportPreviewProps {
  snapshot: ProfessionalReportDTO;
  versionNumber: number;
  generatedAt: string;
}

export function ProfessionalReportPreview({
  snapshot,
  versionNumber,
  generatedAt,
}: ProfessionalReportPreviewProps) {
  const {
    cover,
    executiveSummary,
    texture,
    physics,
    chemistry,
    salinity,
    sodicity,
    irrigation,
    agronomic,
    recommendations,
    notes,
    appendix,
    completeness,
  } = snapshot;

  const scope = reportLevelScope(
    cover.declaredTestLevel ?? cover.testLevel
  );
  const hasMediumLongRecs =
    recommendations.medium.length > 0 || recommendations.long.length > 0;
  const hasAnyRecs =
    recommendations.short.length > 0 || hasMediumLongRecs;

  return (
    <Box sx={{ backgroundColor: "#FFFFFF", color: "text.primary" }}>
      <PrintStyles />

      {/* ── Page 1: Cover ────────────────────────────────── */}
      <ReportCoverPage cover={cover} versionNumber={versionNumber} />

      {/* ── Page 2+: Main report content ─────────────────── */}
      <Box sx={{ p: { xs: 2, md: 4 } }}>
      <Stack spacing={4}>

        <ReportSection 
          id="executive" 
          title="Executive Summary" 
          subtitle="Headline findings and soil rating"
          action={<StatusBadge status={executiveSummary.overallRating} />}
          noBreak
        >
          <Stack component="ul" sx={{ pl: 3, m: 0 }} spacing={1}>
            {executiveSummary.headlineFindings.map((line, i) => (
              <li key={i}><Typography variant="body2">{line}</Typography></li>
            ))}
          </Stack>
        </ReportSection>

        <ReportSection id="texture" title="Soil Texture" subtitle="Physical composition and USDA classification">
          <ReportTable rows={[
            { label: "USDA Texture Class", value: texture.usdaClass },
            { label: "Sand Content", value: texture.sandPercent, unit: "%" },
            { label: "Silt Content", value: texture.siltPercent, unit: "%" },
            { label: "Clay Content", value: texture.clayPercent, unit: "%" },
            { label: "Organic Matter", value: texture.organicMatterPercent, unit: "%" },
          ]} />
        </ReportSection>

        <ReportSection id="physics" title="Physical Properties" subtitle="Hydraulic characteristics and water retention">
          <ReportTable rows={[
            { label: "Field Capacity", value: physics.fieldCapacity, unit: physics.units.moisture },
            { label: "Wilting Point", value: physics.wiltingPoint, unit: physics.units.moisture },
            { label: "Plant-Available Water (PAW)", value: physics.plantAvailableWater, unit: physics.units.moisture },
            { label: "Bulk Density (used)", value: physics.bulkDensity, unit: physics.units.bulkDensity },
            { label: "Porosity", value: physics.porosity, unit: physics.units.porosity },
            { label: "Saturation", value: physics.saturation, unit: physics.units.saturation },
            { label: "Saturated Conductivity (Ksat)", value: physics.saturatedConductivity, unit: physics.units.conductivity },
          ]} />
          {physics.bulkDensityTrace && (
            <ScientificDisclaimer 
              content={`Bulk density traceability: Used (ρDF) is ${physics.bulkDensityTrace.used} ${physics.bulkDensityTrace.unit} sourced from ${physics.bulkDensityTrace.source}. Predicted (ρN) is ${physics.bulkDensityTrace.predicted} ${physics.bulkDensityTrace.unit} (Saxton-Rawls texture-OM estimate).`}
            />
          )}
        </ReportSection>

        <Box className="page-break" />

        <ReportSection
          id="charts"
          title="Scientific Visualisations"
          subtitle={
            scope.chartScope === "texture_only"
              ? "USDA texture triangle (Preliminary evidence scope)"
              : "Graphical analysis of soil properties"
          }
        >
          <ReportChartsSection
            soilTestId={snapshot.source.test.id}
            reportSnapshot={snapshot}
            chartScope={scope.chartScope}
          />
        </ReportSection>

        <ReportSection
          id="chemistry"
          title="Soil Chemistry"
          subtitle={
            scope.showExchangeChemistry
              ? `Analysis mode: ${chemistry.calculationMode ?? "Unknown"}`
              : "Preliminary panel — pH, salinity evidence, and organic matter"
          }
        >
          <ReportTable rows={[
            { label: "pH (H2O)", value: chemistry.pH },
            { label: "Electrical Conductivity (ECe)", value: chemistry.ece, unit: "dS/m" },
            { label: "Organic Matter", value: chemistry.organicMatter, unit: "%" },
            ...(scope.showExchangeChemistry
              ? [
                  { label: "CEC (cmol+/kg)", value: chemistry.cec, unit: chemistry.exchangeableCations?.unit || "" },
                  { label: "Nitrogen (N)", value: chemistry.macroNutrients.n, unit: "mg/kg" },
                  { label: "Phosphorus (P)", value: chemistry.macroNutrients.p, unit: "mg/kg" },
                  { label: "Potassium (K)", value: chemistry.macroNutrients.k, unit: "mg/kg" },
                ]
              : []),
          ]} />
          {scope.showExchangeChemistry &&
            chemistry.cecSource &&
            chemistry.cecSource !== "LAB" && (
            <ScientificDisclaimer 
              severity="warning"
              title="Provisional CEC Disclosure"
              content={`CEC provenance: ${chemistry.cecSource}. This value was derived or estimated, not directly measured by the laboratory.`}
            />
          )}
          {!scope.showExchangeChemistry ? (
            <ScientificDisclaimer
              title="Preliminary chemistry scope"
              content="Cation exchange capacity, exchangeable bases, and nutrient panel values are not assessed at the Preliminary test level. Upgrade to Moderate or Advanced for a full chemistry report."
            />
          ) : null}
        </ReportSection>

        <ReportSection
          id="salinity-sodicity"
          title={scope.showSodicityAssessment ? "Salinity & Sodicity" : "Salinity"}
          subtitle={
            scope.showSodicityAssessment
              ? "Hazard and severity assessment"
              : "Salinity from Preliminary EC/TDS evidence (sodicity requires Moderate+)"
          }
        >
          <Grid container spacing={4}>
            <Grid item xs={12} md={scope.showSodicityAssessment ? 6 : 12}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Salinity Hazard</Typography>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <StatusBadge status={salinity.severity} />
                <Typography variant="body2" color="text.secondary">{salinity.riskLabel}</Typography>
              </Stack>
              <Typography variant="body2">ECe: {fmtSoil(salinity.ece, 2)} dS/m</Typography>
            </Grid>
            {scope.showSodicityAssessment ? (
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Sodicity Hazard</Typography>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <StatusBadge status={sodicity.severity} />
                <Typography variant="body2" color="text.secondary">{sodicity.riskLabel}</Typography>
              </Stack>
              <Typography variant="body2">SAR: {fmtSoil(sodicity.sar, 2)} · ESP: {fmtSoil(sodicity.esp, 2)} %</Typography>
            </Grid>
            ) : (
            <Grid item xs={12}>
              <ScientificDisclaimer
                title="Sodicity not assessed"
                content={sodicity.recommendation}
              />
            </Grid>
            )}
          </Grid>
        </ReportSection>

        <ReportSection
          id="agronomy"
          title="Agronomic Interpretation"
          subtitle={
            scope.showCationAgronomy
              ? "Suitability and classifications"
              : "Preliminary classifications (pH, salinity, water, drainage, OM)"
          }
        >
          <AgronomicCategoriesBlock categories={agronomic.categories} />
          {scope.showTextureSuitability ? (
          <Box sx={{ mt: 3 }}>
            <SuitabilityBlock suitability={agronomic.suitability} />
          </Box>
          ) : null}
        </ReportSection>

        {hasAnyRecs ? (
        <ReportSection
          id="recommendations"
          title="Interpretation notes"
          subtitle={
            scope.showMediumLongRecommendations
              ? "Structured notes by time horizon"
              : "Short-horizon notes only (Preliminary evidence scope)"
          }
        >
          <RecommendationGroup horizon="SHORT" items={recommendations.short} />
          {scope.showMediumLongRecommendations ? (
            <>
              <RecommendationGroup horizon="MEDIUM" items={recommendations.medium} />
              <RecommendationGroup horizon="LONG" items={recommendations.long} />
            </>
          ) : null}
        </ReportSection>
        ) : null}

        <ReportSection id="notes" title="Evidence & Methodology" subtitle="Completeness, warnings and input transparency">
          <NotesBlock notes={notes} />
          {completeness && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>Evidence Contract: {completeness.level.declaredLevel}</Typography>
              <Typography variant="body2" color="text.secondary">{completeness.level.statement}</Typography>
            </Box>
          )}
          <Box sx={{ mt: 3 }}>
            <ScientificDisclaimer
              title="Saxton-Rawls Water Retention Model"
              content="Soil water retention values (FC, WP, Ksat) are derived from the Saxton-Rawls pedotransfer function — a texture- and organic-matter-based statistical estimate. These are not laboratory-measured retention curves."
            />
            <ScientificDisclaimer
              title="Validation Dataset"
              content="Internal validation uses a synthetic benchmark dataset against known reference samples. This is not external laboratory validation and should not be represented as independently certified."
            />
            <ScientificDisclaimer
              title="Calibration Profile"
              content="FLAHA_DEFAULT is the active calibration control profile. This is an internal FlahaSOIL profile, not an internationally standardised external reference."
            />
            <ScientificDisclaimer
              title="Electrical Conductivity Method"
              content="ECe (saturation extract) and EC 1:5 (dilution) are not directly interchangeable. Conversion factors are soil-texture-dependent approximations. Confirm which method was used by your laboratory."
            />
            <ScientificDisclaimer
              title="Nutrient Interpretation"
              content="Nutrient sufficiency ranges are method-, crop-, and region-dependent. Interpretation thresholds applied here reflect general agronomic guidelines and may not match your regional standards or laboratory's reference ranges."
            />
            <ScientificDisclaimer
              title="Missing Data"
              content="Parameters marked as unavailable or estimated reflect absent laboratory measurements. Missing evidence means missing analytical confidence. Do not interpret estimated values with the same confidence as measured values."
            />
          </Box>
        </ReportSection>

        <ReportFooter
          schemaVersion={snapshot.schemaVersion}
          versionNumber={versionNumber}
          generatedAt={generatedAt}
          reportNumber={cover.reportNumber}
        />
      </Stack>
      </Box>
    </Box>
  );
}
